<script setup lang="ts">
import { mdiPlay, mdiFileDocumentOutline, mdiPaletteOutline } from '@mdi/js'

// #719 観点D: モバイルのゆりかご read-only ソースビューア。
// 「読む・遊ぶは出す／書く（写経・編集）は出さない」ポリシーに沿い、Monaco の
// エディタ（PC 用・重い）ではなく、素の <pre> でソースを表示するだけの軽量ビューア。
// 「実行」で親に run を通知し、親が createProjectSession → PlayDialog を没入で開く。
//
// #721: .pyxres / .pyxpal（Pyxel リソース = 絵・音の素材）はコードではないので
// テキストは出さないが、「素材ファイルがある」ことは示す（タブに出し、選ぶと
// 素材である旨のパネルを表示。実物は「実行する」で確認できると案内）。
const props = defineProps<{
  projectId: string
  title?: string
}>()

const modelValue = defineModel<boolean>()
const emit = defineEmits<{ run: [projectId: string] }>()

const api = useApi()

interface ViewFile {
  name: string
  kind: 'text' | 'resource'
  code: string | null // 遅延ロード（未取得は null）。resource は読み込まない。
  loading: boolean
}

const files = ref<ViewFile[]>([])
const activeIdx = ref(0)
const loadingProject = ref(false)
const errorMsg = ref('')

const TEXT_EXT = ['.py', '.txt', '.md', '.json', '.toml', '.cfg']
// .ogg / .png はメディアアセット (#727)。バイナリなので中身は出さず素材として案内。
const RESOURCE_EXT = ['.pyxres', '.pyxpal', '.ogg', '.png']
const isTextFile = (name: string): boolean =>
  TEXT_EXT.some(ext => name.toLowerCase().endsWith(ext))
const isResourceFile = (name: string): boolean =>
  RESOURCE_EXT.some(ext => name.toLowerCase().endsWith(ext))
const fileKind = (name: string): 'text' | 'resource' | null =>
  isTextFile(name) ? 'text' : isResourceFile(name) ? 'resource' : null

const loadFileCode = async (idx: number) => {
  const f = files.value[idx]
  // resource（.pyxres 等）はバイナリなのでソース取得しない。
  if (!f || f.kind !== 'text' || f.code !== null || f.loading) return
  f.loading = true
  try {
    const res = await api.getProjectFile(props.projectId, f.name)
    f.code = res.code
  }
  catch (e) {
    console.error('[MobileSourceViewer] getProjectFile failed', e)
    f.code = '// このファイルは読み込めませんでした。'
  }
  finally {
    f.loading = false
  }
}

const openProject = async () => {
  loadingProject.value = true
  errorMsg.value = ''
  try {
    const proj = await api.getProject(props.projectId)
    files.value = proj.files
      .map(f => ({ name: f.name, kind: fileKind(f.name) }))
      .filter((f): f is { name: string, kind: 'text' | 'resource' } => f.kind !== null)
      .map(f => ({ name: f.name, kind: f.kind, code: null, loading: false }))
    activeIdx.value = 0
    if (files.value.length > 0) await loadFileCode(0)
  }
  catch (e) {
    console.error('[MobileSourceViewer] getProject failed', e)
    errorMsg.value = 'この魔導書は読み込めませんでした。'
  }
  finally {
    loadingProject.value = false
  }
}

const onSelectFile = async (idx: number) => {
  activeIdx.value = idx
  await loadFileCode(idx)
}

// ダイアログを開くたびに読み直す（別の魔導書で開き直される場合に対応）。
watch(modelValue, (open) => {
  if (open) openProject()
})

const activeFile = computed(() => files.value[activeIdx.value] ?? null)
</script>

<template>
  <DialogMobileDialog
    v-model="modelValue"
    :title="title || '魔導書を読む'"
  >
    <div class="source-viewer">
      <div
        v-if="loadingProject"
        class="source-viewer__state"
      >
        読み込み中…
      </div>
      <div
        v-else-if="errorMsg"
        class="source-viewer__state"
      >
        {{ errorMsg }}
      </div>
      <template v-else>
        <!-- ファイルタブ（複数ファイル時のみ） -->
        <div
          v-if="files.length > 1"
          class="source-viewer__tabs"
        >
          <button
            v-for="(f, i) in files"
            :key="f.name"
            type="button"
            class="source-viewer__tab"
            :class="{ 'source-viewer__tab--active': i === activeIdx }"
            @click="onSelectFile(i)"
          >
            <v-icon
              :icon="f.kind === 'resource' ? mdiPaletteOutline : mdiFileDocumentOutline"
              size="x-small"
            />
            {{ f.name }}
          </button>
        </div>

        <!-- 素材ファイル（.pyxres 等）: コードではないので中身は出さず、素材である旨を案内 -->
        <div
          v-if="activeFile?.kind === 'resource'"
          class="source-viewer__resource"
        >
          <v-icon
            :icon="mdiPaletteOutline"
            size="44"
            class="source-viewer__resource-icon"
          />
          <p class="source-viewer__resource-title">
            絵と音の魔法素材
          </p>
          <p class="source-viewer__resource-note">
            これはゲームのスプライトやサウンドが入った素材ファイル（{{ activeFile.name }}）です。
            コードではないので中身の文字は表示していません。<br>
            「実行する」を押すと、素材が使われた実際の見た目や音を確かめられます。
          </p>
        </div>

        <div
          v-else
          class="source-viewer__code-wrap"
        >
          <div
            v-if="activeFile?.loading"
            class="source-viewer__state"
          >
            読み込み中…
          </div>
          <pre
            v-else
            class="source-viewer__code"
          ><code>{{ activeFile?.code ?? '' }}</code></pre>
        </div>
      </template>
    </div>

    <template #actions>
      <v-btn
        color="primary"
        variant="flat"
        :prepend-icon="mdiPlay"
        :disabled="loadingProject || !!errorMsg"
        @click="emit('run', projectId)"
      >
        実行する
      </v-btn>
    </template>
  </DialogMobileDialog>
</template>

<style scoped>
.source-viewer {
  display: flex;
  flex-direction: column;
  min-height: 0;
  font-family: "Noto Sans JP", sans-serif;
}

.source-viewer__state {
  padding: 24px 8px;
  text-align: center;
  color: #607d8b;
}

.source-viewer__tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding-bottom: 6px;
  scrollbar-width: thin;
}

.source-viewer__tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  background: #f5f5f5;
  font-size: 0.78rem;
  white-space: nowrap;
  cursor: pointer;
}

.source-viewer__tab--active {
  background: #e3f2fd;
  border-color: #64b5f6;
  font-weight: 700;
}

.source-viewer__code-wrap {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: #1e1e1e;
  overflow: auto;
  max-height: 62dvh;
}

.source-viewer__code {
  margin: 0;
  padding: 12px;
  font-family: "SF Mono", Menlo, Consolas, "Courier New", monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #e0e0e0;
  white-space: pre;
  tab-size: 4;
}

/* 素材ファイル（.pyxres 等）のプレースホルダ */
.source-viewer__resource {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 28px 16px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: linear-gradient(135deg, #f4ede0, #e7dcc7);
}

.source-viewer__resource-icon {
  color: #a3701f;
}

.source-viewer__resource-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #5c4326;
}

.source-viewer__resource-note {
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.6;
  color: #6b5a44;
}
</style>
